/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'
import { css } from 'styled-components'
import { useMemo } from 'use-memo-one'

import { Array, AsyncStateN } from '@blade/std-lib'
import { useBorderBoxSize } from '@blade/ui-kit'
import { useBreakpoint } from '@root/shared/hooks'
import { useTranslation } from '@root/shared/localization'
import { createColumnGridTemplate, mediaMaxMobile } from '@root/shared/ui-kit'
import { DataTableHeader } from '@root/shared/ui-kit/data-table/ui/data-table-header'
import { DataTableLoading } from '@root/shared/ui-kit/data-table/ui/data-table-loading'
import { DataTableMessage } from '@root/shared/ui-kit/data-table/ui/data-table-message'

import { reatomUserProfileHoldingsTable } from '../atoms/reatom-user-profile-holdings-table'

import { HoldingsTableRow } from './holdings-table-row'

import type { DelugeId } from '@root/entities/user-short'

type Props = {
  delugeId: DelugeId
}

export const UserProfileHoldingsTable = ({ delugeId }: Props) => {
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()

  const tableModel = useMemo(() => {
    return reatomUserProfileHoldingsTable({
      delugeId,
      smallScreen: breakpoint === 'mobile',
    })
  }, [delugeId, breakpoint])

  const [bodyEl, setBodyEl] = useState<HTMLDivElement | undefined>(undefined)
  const bodyHeight = useBorderBoxSize(bodyEl).blockSize

  const [columns] = useAtom(tableModel.columns)
  const [walletsState] = useAtom(tableModel)
  const [expandedWallets] = useAtom(tableModel.expandedWallets)
  const toggleExpandedWallet = useAction(tableModel.toggleExpandedWallet)
  const clearExpandedWallets = useAction(tableModel.clearExpandedWallets)
  const load = useAction(tableModel.load)

  const rowStyle = useMemo(
    (): React.CSSProperties => ({
      gridTemplateColumns: createColumnGridTemplate(columns),
    }),
    [columns],
  )

  const hasExpandedWallets = expandedWallets.size > 0
  const noItems = AsyncStateN.matchWaiting(walletsState, {
    Success: (data) => Array.isEmpty(data.data),
    Error: () => true,
    Waiting: () => true,
  })

  return (
    <Root>
      <DataTableHeader
        model={tableModel}
        columns={columns}
        context={undefined}
      />
      <Rows
        data-empty={noItems}
        data-has-expanded={hasExpandedWallets}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            clearExpandedWallets()
          }
        }}
        ref={(el) => {
          setBodyEl(el ?? undefined)
        }}
      >
        {AsyncStateN.matchWaiting(walletsState, {
          Success: (data) => {
            if (Array.isEmpty(data.data)) {
              return (
                <DataTableMessage
                  title={t('common.no-data-found')}
                  retryText={t('common.refresh')}
                  onRetry={load}
                />
              )
            }

            return data.data.map((wallet) => (
              <HoldingsTableRow
                bodyHeight={bodyHeight}
                style={rowStyle}
                key={wallet.address}
                wallet={wallet}
                expanded={expandedWallets.has(wallet)}
                onExpandToggle={() => toggleExpandedWallet(wallet)}
                faded={hasExpandedWallets && !expandedWallets.has(wallet)}
              />
            ))
          },
          Error: () => (
            <DataTableMessage
              title={t('common.error')}
              description={t('common.something-went-wrong')}
              onRetry={load}
            />
          ),
          Waiting: () => <DataTableLoading />,
          // Success: () => <DataTableLoading />,
        })}
      </Rows>
    </Root>
  )
}

const Root = styled.div`
  height: 100%;
  min-width: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;

  --data-table-header-padding-inline: 12px;

  ${mediaMaxMobile(css`
    width: 100vw;
  `)}
`

const Rows = styled.div`
  background: var(--background-1);
  display: grid;
  grid-auto-flow: row;
  min-width: 0;
  overflow-y: auto;
  min-height: 0;

  align-content: start;

  &[data-has-expanded='true'] {
    overflow-y: hidden;
  }
  &[data-empty='true'] {
    align-content: stretch;
  }
`
